import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Plus, Upload, Check, Pencil, AlertTriangle, Sparkles, Boxes } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { photoUrl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Card, Chip, Button, Skeleton, EmptyState, Toast, Modal, Input } from "@/ui";

function InlinePrice({ product, shopId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String((product.priceCentavos || 0) / 100));
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e?.stopPropagation?.();
    setBusy(true);
    try {
      const r = await api.put(`/products/${product.id}/price`, { shopId, price: Number(value) });
      onSaved(product.id, r.data.priceCentavos);
      setEditing(false);
    } catch { /* keep editing on failure */ }
    finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <button className="btn btn-ghost btn-sm num" data-testid={`price-edit-${product.id}`}
        onClick={(e) => { e.stopPropagation(); setEditing(true); }} title="tap to change price">
        {product.price}<Pencil size={13} strokeWidth={1.75} style={{ marginLeft: 6, opacity: 0.6 }} />
      </button>
    );
  }
  return (
    <span className="row" style={{ gap: 6 }} onClick={(e) => e.stopPropagation()}>
      <input className="input num" data-testid={`price-input-${product.id}`} autoFocus value={value}
        inputMode="decimal" style={{ width: 90, padding: "4px 8px" }}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(e); if (e.key === "Escape") setEditing(false); }} />
      <Button size="sm" variant="primary" data-testid={`price-save-${product.id}`} loading={busy} onClick={save}>
        <Check size={14} strokeWidth={2} />
      </Button>
    </span>
  );
}

export default function Products() {
  const { shopId, shopName, can } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [restockOpen, setRestockOpen] = useState(false);
  const [adds, setAdds] = useState({});
  const [savingRestock, setSavingRestock] = useState(false);
  const canEdit = !!can?.add_product_change_price;

  const doRestock = async () => {
    const items = Object.entries(adds)
      .map(([productId, v]) => ({ productId, addOnHand: parseInt(v, 10) }))
      .filter((it) => it.addOnHand > 0);
    if (!items.length) { setRestockOpen(false); return; }
    setSavingRestock(true);
    try {
      await api.post("/products/bulk-restock", { shopId, items });
      const r = await api.get(`/products?shopId=${shopId}`);
      setProducts(r.data.products);
      setRestockOpen(false); setAdds({});
      setToast(`restocked ${items.length} item(s)`);
      setTimeout(() => setToast(""), 2500);
    } catch (e) { setError(friendlyError(e)); }
    finally { setSavingRestock(false); }
  };

  useEffect(() => {
    if (!shopId) { navigate("/pick-shop", { replace: true }); return; }
    setLoading(true);
    api.get(`/products?shopId=${shopId}`).then((r) => setProducts(r.data.products)).catch((e) => setError(friendlyError(e))).finally(() => setLoading(false));
  }, [shopId, navigate]);

  const onPriceSaved = (id, priceCentavos) => {
    setProducts((ps) => ps.map((p) => p.id === id ? { ...p, priceCentavos, price: pesoStr(priceCentavos) } : p));
    setToast("price updated");
    setTimeout(() => setToast(""), 2500);
  };

  return (
    <Layout shopName={shopName}>
      <div className="row-between" style={{ marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
        <h1 className="t-h1">items</h1>
        {canEdit ? (
          <div className="row" style={{ gap: 8 }}>
            <Button data-testid="import-items-btn" onClick={() => navigate("/products/import")}>
              <Upload size={16} strokeWidth={1.75} />import
            </Button>
            <Button data-testid="bulk-restock-btn" onClick={() => setRestockOpen(true)}>
              <Boxes size={16} strokeWidth={1.75} />restock
            </Button>
            <Button variant="primary" data-testid="quick-add-btn" onClick={() => navigate("/products/add")}>
              <Sparkles size={16} strokeWidth={1.75} />add items
            </Button>
          </div>
        ) : null}
      </div>
      <p className="muted" style={{ marginBottom: 24 }}>everything you sell, with how many are left.</p>
      {error ? <div className="err-text" data-testid="products-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      {loading ? (
        <div className="grid grid-3"><Skeleton variant="card" /><Skeleton variant="card" /><Skeleton variant="card" /></div>
      ) : products.length === 0 ? (
        <EmptyState icon={<Package size={20} strokeWidth={1.75} />}
          title="you have no items yet — add your first item so people can start buying"
          action={canEdit ? <Button variant="primary" onClick={() => navigate("/products/add")}><Sparkles size={16} strokeWidth={1.75} />add items</Button> : null} />
      ) : (
        <div className="grid grid-3" data-testid="products-list">
          {products.map((p) => (
            <Card key={p.id} interactive data-testid={`product-${p.id}`} onClick={() => navigate(`/products/${p.id}`)}
              footer={<div className="row-between">
                <span className="muted-3 t-small">{p.category}</span>
                {p.soldOut ? <Chip tone="bad" dot data-testid={`soldout-${p.id}`}>sold out</Chip>
                  : p.lowStock ? <Chip tone="warn" dot data-testid={`lowstock-${p.id}`}><AlertTriangle size={13} strokeWidth={2} />{p.available} left</Chip>
                  : <span className="muted-3 t-small num">{p.available} left</span>}
              </div>}>
              {p.photos?.[0] ? <img className="thumb" src={photoUrl(p.photos[0])} alt={p.name} loading="lazy" />
                : <div className="thumb" style={{ display: "grid", placeItems: "center", background: "var(--surface-2)" }}><Package size={28} strokeWidth={1.25} style={{ opacity: 0.4 }} /></div>}
              <div className="row-between" style={{ marginTop: 12 }}>
                <span className="t-h3">{p.name}{!p.active ? <span className="muted-3 t-small"> · hidden</span> : null}</span>
                {canEdit ? <InlinePrice product={p} shopId={shopId} onSaved={onPriceSaved} /> : <span className="t-h3 num">{p.price}</span>}
              </div>
            </Card>
          ))}
        </div>
      )}
      <Modal open={restockOpen} title="Bulk restock" onClose={() => setRestockOpen(false)}
        footer={<>
          <Button data-testid="restock-cancel" onClick={() => setRestockOpen(false)}>cancel</Button>
          <Button variant="primary" data-testid="restock-save" loading={savingRestock} onClick={doRestock}>add to stock</Button>
        </>}>
        <p className="muted t-small" style={{ marginBottom: 12 }}>Type how many more of each item you have on hand. Leave blank to skip.</p>
        <div className="stack" data-testid="restock-list" style={{ gap: 8, maxHeight: 360, overflowY: "auto" }}>
          {products.map((p) => (
            <div key={p.id} className="row-between" data-testid={`restock-row-${p.id}`} style={{ gap: 12 }}>
              <div className="row" style={{ gap: 8, minWidth: 0 }}>
                {p.photos?.[0] ? <img src={photoUrl(p.photos[0])} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: "cover" }} /> : <Package size={18} style={{ opacity: 0.4 }} />}
                <div style={{ minWidth: 0 }}>
                  <div className="t-small" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                  <div className="muted-3 t-small num">{p.available} left</div>
                </div>
              </div>
              <Input data-testid={`restock-input-${p.id}`} inputMode="numeric" placeholder="+0" style={{ width: 80 }}
                value={adds[p.id] || ""} onChange={(e) => setAdds((a) => ({ ...a, [p.id]: e.target.value.replace(/[^0-9]/g, "") }))} />
            </div>
          ))}
        </div>
      </Modal>
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="products-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}

function pesoStr(centavos) {
  const whole = Math.floor(Math.abs(centavos) / 100);
  const frac = String(Math.abs(centavos) % 100).padStart(2, "0");
  return `\u20b1${whole.toLocaleString()}.${frac}`;
}
